/**
 * Guardian agent voice HTTP helpers (Whisper STT + AWOS TTS).
 *
 * The agent enforces `Authorization: Bearer <guardian token>` on both voice
 * endpoints when VOICE_AUTH_REQUIRED=true: 401 = missing/invalid/expired
 * token, 503 = sign-in verification unavailable, 413 = audio over 10 MB.
 * Callers distinguish these via `VoiceApiError.status`.
 *
 * The client-side "you must have a token before we even try" gate only
 * applies on the Guardian surface (`scopeProfile === 'guardian'`), since that
 * is the only build where `useGuardianAuth` ever loads a token. Other scope
 * profiles (e.g. an internal copilot build) send the request without an
 * Authorization header and let the server decide — a 401 from the server
 * still flows through the normal VoiceApiError(401) handling.
 */

/** Thrown by the voice helpers below; `status` is an HTTP status code, or
 * the string 'auth-required' when no token was available client-side. */
export class VoiceApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'VoiceApiError';
    this.status = status;
  }
}

function agentHttpBaseFromWs(wsUrl) {
  try {
    const parsed = new URL(wsUrl);
    parsed.protocol = parsed.protocol === 'wss:' ? 'https:' : 'http:';
    parsed.pathname = '';
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return process.env.REACT_APP_AGENT_HTTP_URL || 'http://localhost:9000';
  }
}

/**
 * Throws VoiceApiError('auth-required') without hitting the network when the
 * Guardian scope requires a token client-side but none is available. Every
 * other scope profile never loads a Guardian token, so it is never gated
 * here — the request goes out and the server decides.
 */
function requireToken(token, scopeProfile) {
  if (scopeProfile === 'guardian' && !token) {
    throw new VoiceApiError('Sign in required for voice features.', 'auth-required');
  }
}

/** Omits Authorization entirely (rather than sending "Bearer undefined") when no token is present. */
function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function postVoiceRequest(url, options) {
  try {
    return await fetch(url, options);
  } catch (err) {
    throw new VoiceApiError(err.message || 'Network request failed', 0);
  }
}

export async function transcribeVoiceBlob(wsUrl, blob, token, scopeProfile) {
  requireToken(token, scopeProfile);
  const base = agentHttpBaseFromWs(wsUrl);
  const form = new FormData();
  const filename = blob.type?.includes('wav') ? 'audio.wav' : 'audio.webm';
  form.append('file', blob, filename);
  const res = await postVoiceRequest(`${base}/voice/transcribe`, {
    method: 'POST',
    headers: authHeaders(token),
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new VoiceApiError(
      body.detail || body.message || `Transcription failed (${res.status})`,
      res.status,
    );
  }
  if (!body.text) {
    throw new VoiceApiError('Empty transcript', res.status);
  }
  return body.text;
}

export async function speakAwosText(wsUrl, text, token, scopeProfile) {
  requireToken(token, scopeProfile);
  const base = agentHttpBaseFromWs(wsUrl);
  const res = await postVoiceRequest(`${base}/voice/speak-json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify({ text, awos: true }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new VoiceApiError(
      body.detail || body.message || `Speech synthesis failed (${res.status})`,
      res.status,
    );
  }
  return {
    awosSpeakText: body.awos_speak_text,
    audioBase64: body.audio_base64,
    contentType: body.content_type || 'audio/mpeg',
  };
}

export function playBase64Audio(audioBase64, contentType = 'audio/mpeg') {
  const binary = atob(audioBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: contentType });
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  const cleanup = () => URL.revokeObjectURL(url);
  audio.addEventListener('ended', cleanup);
  audio.addEventListener('error', cleanup);
  const playPromise = audio.play();
  return { audio, playPromise };
}
